import { useEffect, useState } from 'react';

import type { Company } from '../../../domain/company';
import { useCompanyEcommerceReportQuery } from '../../../features/company/useCompanyEcommerceReportQuery';
import useEcommerceReportMutation from '../../../features/company/useEcommerceReportMutation';
import { translate } from '../../../services/i18n';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';
import { Metric } from '../Metric';
import { SectionHeader } from '../SectionHeader';
import { StyledSectionView } from '../styles';

import { NotEcommerceView } from './NotEcommerceView';
import { StyledMetricsContainer } from './styles';

export const Ecommerce = ({ company }: { company: Company }) => {
  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();
  const { monthlyTransactionsRange, onlineRevenueRange, ecommerceType, domain } = company;
  const { data } = useCompanyEcommerceReportQuery(domain);
  const isEcommerceReported = data?.isEcommerceReported;

  const { mutate, isPending: isReportLoading } = useEcommerceReportMutation(domain);

  const [isReportSent, setIsReportSent] = useState(false);
  const [hasReportStarted, setHasReportStarted] = useState(false);

  const handleReport = () => {
    mutate(domain);
    setHasReportStarted(true);
    dispatchTrackEventWithLocation('company data', 'give feedback', `false negative ecommerce/${domain}`);
  };

  useEffect(() => {
    if (hasReportStarted && !isReportLoading) {
      setIsReportSent(true);
      setHasReportStarted(false);
    }
  }, [isReportLoading, hasReportStarted]);

  const renderContent = () => {
    if (!ecommerceType && !monthlyTransactionsRange && !onlineRevenueRange) {
      return (
        <NotEcommerceView
          isAlreadyReported={!!isEcommerceReported}
          onReport={handleReport}
          isReportSent={isReportSent}
          isReportLoading={isReportLoading}
        />
      );
    }

    return (
      <StyledMetricsContainer>
        <Metric
          name={translate('company.ecommerce.transactions')}
          value={monthlyTransactionsRange}
          iconName="package"
        />
        <Metric
          name={translate('company.ecommerce.revenue')}
          value={onlineRevenueRange ? `$${onlineRevenueRange}` : 'N/A'}
          iconName="payments"
        />
        <Metric name={translate('company.ecommerce.type')} value={ecommerceType} iconName="trio-shape" />
      </StyledMetricsContainer>
    );
  };

  return (
    <StyledSectionView data-automation="Ecommerce Section" withPadding>
      <SectionHeader withSectionPadding title={translate('company.ecommerce')} isBeta />
      {renderContent()}
    </StyledSectionView>
  );
};
